import { type MouseEvent as ReactMouseEvent } from "react";
import {
  AnchoredPopup,
  CarbonsixIcon,
  ChevronIcon,
  GithubIcon,
  LogOutIcon,
  Text,
  useAnchoredPopupAnchor,
} from "@carbon6/c6-frontend-design";
import { hatcheryTextModes } from "@/app/theme";
import { HatcheryLogo } from "./HatcheryLogo";
import styles from "./SidebarAccountMenu.module.css";

type SidebarAccountMenuProps = {
  // 접힘 레일에서는 trigger 를 Hatchery 로고(맨 아래)로 렌더한다. 펼친 상태에서는 타이틀 옆 chevron 으로 렌더한다.
  collapsed: boolean;
  userName: string;
  // Admin 일 때만 값이 있다(이름 우측 끝 칩). 그 외에는 null 이고 표시하지 않는다.
  adminRoleLabel: string | null;
  // 소속 그룹 요약(이름 아래 줄)이다. 없으면 표시하지 않는다.
  groupLabel: string | null;
  // 팝업이 처음 열릴 때 호출한다. 소속 그룹 lazy 로드를 시작한다.
  onOpen: () => void;
  onLogout: () => void;
};

// 브랜드(Hatchery) 옆 chevron 트리거와 계정 팝업이다. 팝업은 다음을 위에서부터 표시한다:
//   - Carbonsix 조직 헤더(로고+이름·소속 그룹)
//   - 계정 행(GitHub 마크·이름·admin 역할)
//   - 로그아웃
// 설정 진입은 이 팝업이 아니라 사이드바 하단 설정 버튼이 가진다. 데이터·핸들러는 PageBase 가 주입한다.
export function SidebarAccountMenu({
  collapsed,
  userName,
  adminRoleLabel,
  groupLabel,
  onOpen,
  onLogout,
}: SidebarAccountMenuProps) {
  const anchor = useAnchoredPopupAnchor();

  function handleTriggerClick(event: ReactMouseEvent<HTMLButtonElement>) {
    if (anchor.open) {
      anchor.close();
      return;
    }
    onOpen();
    anchor.openFromElement(event);
  }

  function handleLogoutClick() {
    anchor.close();
    onLogout();
  }

  return (
    <>
      <button
        type="button"
        className={styles.trigger}
        data-collapsed={collapsed ? "true" : "false"}
        aria-label="계정 메뉴"
        aria-haspopup="dialog"
        aria-expanded={anchor.open}
        data-open={anchor.open ? "true" : "false"}
        onClick={handleTriggerClick}
      >
        {collapsed ? (
          <HatcheryLogo className={styles.triggerLogo} />
        ) : (
          <ChevronIcon className={styles.triggerIcon} />
        )}
      </button>

      <AnchoredPopup
        open={anchor.open}
        anchor={anchor.anchor}
        onClose={anchor.close}
        placement={collapsed ? "right-end" : "bottom"}
        width="17rem"
        role="dialog"
        aria-label="계정 메뉴"
        // 팝업 배경을 좌측 내비(사이드바)와 같은 surface 톤으로 맞춘다. c6 Panel 기본값은
        // surfaceRaised 다. 그래서 style 로 덮어써 styled-component 기본값보다 우선하게 한다.
        style={{ backgroundColor: "var(--c6-color-bg-surface)" }}
      >
        <AnchoredPopup.Body padding="none">
          <div className={styles.brand}>
            <CarbonsixIcon size={32} className={styles.brandIcon} />
            <div className={styles.brandText}>
              <Text variant={hatcheryTextModes["body-bold"]} className={styles.brandName}>
                carbon6robotics
              </Text>
              {groupLabel ? <span className={styles.brandGroup}>{groupLabel}</span> : null}
            </div>
          </div>

          <div className={styles.accountRow}>
            <GithubIcon className={styles.accountIcon} />
            <Text variant={hatcheryTextModes["body-bold"]} className={styles.accountName}>
              {userName}
            </Text>
            {adminRoleLabel ? <span className={styles.accountRole}>{adminRoleLabel}</span> : null}
          </div>
        </AnchoredPopup.Body>

        <AnchoredPopup.Footer divider={false} className={styles.footer}>
          <button type="button" className={styles.logoutButton} onClick={handleLogoutClick}>
            <LogOutIcon className={styles.logoutIcon} />
            <span className={styles.logoutText}>로그아웃</span>
          </button>
        </AnchoredPopup.Footer>
      </AnchoredPopup>
    </>
  );
}
